import React, { useEffect, useRef, useState } from 'react';
import { Download16 } from '@carbon/icons-react';
import {
  Button,
  InlineLoading,
  SkeletonPlaceholder
} from 'carbon-components-react';
import { ReactComponent as WebMapIcon } from 'assets/icons/web-map.svg';
import { ReactComponent as NoPreviewIcon } from 'assets/icons/no-preview.svg';
import { ResponseThumbnail } from 'components';

export const ResponsePreview = ({
  label,
  isLoading,
  content
}: {
  label: string;
  isLoading: boolean;
  content: React.ReactNode;
}) => {
  return (
    <div className="mobile-view-response-preview-wrapper">
      {isLoading ? (
        <SkeletonPlaceholder className="mobile-view-response-preview-skeleton" />
      ) : (
        <div className="mobile-view-response-preview-card">{content}</div>
      )}
      {isLoading ? (
        <SkeletonPlaceholder className="mobile-view-response-preview-label-skeleton" />
      ) : (
        <div className="mobile-view-response-preview-label">{label}</div>
      )}
    </div>
  );
};

export const ResponseBodyPreview = ({
  onClick,
  enquiryId,
  responseId
}: {
  onClick: () => void;
  enquiryId?: number;
  responseId?: number;
}) => {
  return (
    <div className="mobile-view-response-preview-thumbnail" onClick={onClick}>
      <ResponseThumbnail
        fileType="body"
        responseId={responseId}
        enquiryId={enquiryId}
      />
    </div>
  );
};

export const ResponseWebMapPreview = ({ onClick }: { onClick: () => void }) => {
  return (
    <div className="mobile-view-response-preview-thumbnail" onClick={onClick}>
      <WebMapIcon
        className="mobile-view-response-preview-icon"
        fill="#878D96"
      />
      <div className="mobile-view-response-preview-text">
        A web map is an interactive map showing assets in the job area. Please
        click to access the content.
      </div>
    </div>
  );
};

export const ResponseImagePreview = ({
  onClick,
  fileId,
  responseId,
  enquiryId
}: {
  onClick: () => void;
  fileId?: number;
  responseId?: number;
  enquiryId?: number;
}) => {
  return (
    <div className="mobile-view-response-preview-thumbnail" onClick={onClick}>
      <ResponseThumbnail
        fileType="image"
        fileId={fileId}
        responseId={responseId}
        enquiryId={enquiryId}
      />
    </div>
  );
};

export const ResponsePdfPreview = ({
  onClick,
  fileId,
  responseId,
  enquiryId,
  isLoadingForIOS = false,
  onVisible
}: {
  onClick: (e?: React.MouseEvent<HTMLDivElement>) => void;
  fileId?: number;
  responseId?: number;
  enquiryId?: number;
  isLoadingForIOS?: boolean;
  onVisible?: () => void;
}) => {
  const elementRef = useRef<HTMLDivElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  // Trigger onVisible callback when component becomes visible (for pre-fetching)
  useEffect(() => {
    if (!onVisible || hasBeenVisible) return;

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible) {
          setHasBeenVisible(true);
          onVisible();
        }
      },
      {
        rootMargin: '200px', // Start pre-fetching before it comes into view
        threshold: 0
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [onVisible, hasBeenVisible]);

  return (
    <div
      ref={elementRef}
      className={`mobile-view-response-preview-thumbnail`}
      onClick={isLoadingForIOS ? undefined : onClick}
      style={{
        position: 'relative'
      }}
    >
      {fileId ? (
        <>
          <ResponseThumbnail
            fileType="pdf"
            fileId={fileId}
            responseId={responseId}
            enquiryId={enquiryId}
          />
          {isLoadingForIOS && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                margin: '0 16px'
              }}
            >
              <InlineLoading style={{ width: 'unset' }} />
            </div>
          )}
        </>
      ) : (
        <div className="mobile-view-pdf-placeholder">PDF Preview</div>
      )}
    </div>
  );
};

export const ResponseNoPreview = ({ url }: { url: string }) => {
  return (
    <div className="mobile-view-response-preview-thumbnail">
      <NoPreviewIcon
        className="mobile-view-response-preview-icon"
        fill="#878D96"
      />
      <div className="mobile-view-response-preview-text">
        Preview not supported. Please download to access the content.
      </div>
      <Button
        kind="secondary"
        className="mobile-view-download-button"
        renderIcon={Download16}
        onClick={() => {
          window.open(url, '_blank');
        }}
      >
        Download
      </Button>
    </div>
  );
};

export const ResponseLazyLoader = ({
  children,
  // fallback = null,
  rootMargin = '100px' // Load 100px before coming into view
}: {
  children: React.ReactNode;
  // fallback?: React.ReactNode;
  rootMargin?: string;
}) => {
  const [, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.target) {
          setIsVisible(true);
          setHasBeenVisible(true);
          // Once loaded, we can stop observing
          observer.unobserve(entry.target);
        }
      },
      {
        rootMargin, // Start loading before element comes into view
        threshold: 0
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin]);

  return (
    <div ref={elementRef} className="mobile-view-response-lazy-loader">
      {hasBeenVisible ? (
        children
      ) : (
        <div className="mobile-view-response-pdf-preview">
          <div className="mobile-view-pdf-placeholder">Loading Preview...</div>
        </div>
      )}
    </div>
  );
};
